import { computed, observable, action, makeObservable } from 'mobx'
import _ from 'lodash'
import constants, { selectionTypes, defaultPolygonColor } from 'constants.js'
import { lineString, polygon } from '@turf/helpers'
import area from '@turf/area'
import api from 'api.js'
import Wkt from 'wicket/wicket'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import { turfPointToLatLng } from 'utils.js'
import { distanceBetweenPoints, removeHtml, getDefaultGrayColor } from '../utils'
import { selectionStore, dataStore } from './UIStore'
import i18next from '../i18n'

// Close the shape if the user clicks within
// this many pixels of the shape's starting point
const CLOSING_DISTANCE = 6
const REMOVING_DISTANCE = 9

class Shape {
  @observable name = ''
  @observable points = []

  // is the shape still being drawn?
  @observable drawing = true

  @observable removing = false
  @observable color_id = null
  @observable transparency = 50

  // is the user's cursor in a position where if they clicked,
  // the shape would be closed?
  @observable closing = false
  @observable keys = {}

  @observable invalid = observable.map()
  @observable position = new Position()
  @observable additional_info = []

  constructor () {
    makeObservable(this)
  }

  @computed get finalPoint () {
    return this.closing
      ? (this.points.length > 0 ? this.points[0] : null)
      : this.position.geoJSON
  }

  @computed get lastTwoPointsDistance () {
    return distanceBetweenPoints(this.points, this.finalPoint)
  }

  @computed get pointArray () {
    if (this.drawing && this.points.length > 0) {
      return [
        ..._.map(this.points, point => (point && point.peek ? point.peek() : point)),
        this.finalPoint
      ]
    } else if (this.points.length > 0) {
      return [
        ..._.map(this.points, point => (point && point.peek ? point.peek() : point))
      ]
    } else {
      return null
    }
  }

  // The current shape, which could be a line if the user
  // is still drawing, a polygon, or nothing
  @computed get region () {
    if (this.drawing && this.points.length > 0) {
      return lineString(this.pointArray).geometry
    } else if (this.points.length > 0) {
      return polygon([[
        ...this.pointArray,
        this.pointArray[0]
      ]]).geometry
    } else {
      return null
    }
  }

  @computed get drawingAreaSquareKilometers () {
    if (this.pointArray && this.pointArray.length > 2) {
      return area(polygon([[
        ...this.pointArray,
        this.pointArray[0]
      ]]).geometry) / 1000000
    } else {
      return 0
    }
  }

  @computed get tooLarge () {
    return this.drawingAreaSquareKilometers > constants.MAX_GEOMETRY_SIZE
  }

  mapMousemove (e) {
    if (this.drawing) {
      this.position.latLng = e.latlng

      if (this.points.length > 2) {
        const firstPoint = this.points[0]
        if (firstPoint) {
          const startingPoint = e.target.latLngToLayerPoint(
            turfPointToLatLng(firstPoint)
          )
          const pixelDistance = e.layerPoint.distanceTo(startingPoint)
          this.closing = pixelDistance < CLOSING_DISTANCE
        } else {
          this.closing = false
        }
      } else {
        this.closing = false
      }
      const lastPoint = this.points[this.points.length - 1]
      if (lastPoint) {
        const lastPointLayer = e.target.latLngToLayerPoint(
          turfPointToLatLng(lastPoint)
        )
        const pixelDistance = e.layerPoint.distanceTo(lastPointLayer)
        this.removing = pixelDistance < REMOVING_DISTANCE
      }
    }
  }

  documentKeyup (e) {
    if (e.key === 'Escape' && this.drawing) {
      return this.points.clear()
    }
  }

  documentKeydown (e) {
    if (e.key) {
      const key = e.key.toLowerCase()
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        this.undoLastPoint()
      }
    }
  }

  undoLastPoint () {
    if (this.drawing) {
      this.points.pop()
      this.removing = false
    } else {
      this.drawing = true
    }
  }

  documentDblclick (e) {
    if (this.drawing) {
      e.stopPropagation()
    }
  }

  mapMouseup (e) {
    if (this.closing) {
      this.drawing = false
      return
    }

    if (this.removing) {
      this.undoLastPoint()
    } else {
      this.addPoint()
    }
  }

  addPoint () {
    if (this.drawing && this.position && this.position.geoJSON) {
      this.points.push(this.position.geoJSON)
    }
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.SHAPE,
      data: { focusedInteraction: true }
    }
  }

  @computed get getColor () {
    const item = dataStore.markerColors.find(item => item.id === this.color_id)

    return item ? item.active_color : defaultPolygonColor
  }

  validate (targetGroup) {
    // Check for duplicate names only if we have a real group with geopolygons (not in draft mode)
    const group = targetGroup || (selectionStore.data && selectionStore.data.group) || (dataStore.targetGroups && dataStore.targetGroups[0])
    if (group && group.geopolygons && this.name && _.find(group.geopolygons, { name: this.name.toString() })) {
      this.invalid.set('name', i18next.t('GLOBAL.DUPLICATE_ELEMENT_NAME', { element: i18next.t('GLOBAL.GEOPOLYGON') }))
      return false
    }

    this.invalid.clear()
    if (this.name === null || _.isEmpty(this.name.toString())) {
      this.invalid.set('name', i18next.t('INVALID.REQUIRED'))
    }

    return this.invalid.size === 0
  }

  @catchError
  async add (targetGroupId) {
    if (this.validate()) {
      try {
        const wkt = new Wkt.Wkt()
        wkt.read(JSON.stringify(this.region))
        const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)
        const result = await api.postGeopolygon({
          name: this.name,
          the_geom: wkt.write(),
          target_group: targetGroupId,
          color: effectiveColorId,
          color_transparency: this.transparency
        })
        return result.data
      } catch (e) {
        let error = i18next.t('GLOBAL.AN_ERROR_OCCURRED')
        if (_.get(e, 'response.status') === 400) {
          error = removeHtml(e.response.data[Object.keys(e.response.data)[0]][0])
          error = error.replace('UPGRADEWhy am I getting this?', '')
          this.invalid.set('name', error)
          return false
        } else {
          throw (e)
        }
      }
    } else {
      return null
    }
  }

  /** Add shape to draft mode instead of saving to backend immediately */
  @action
  async addToDraft (poi) {
    if (!this.validate()) {
      return false
    }

    if (!this.region) {
      this.invalid.set('name', i18next.t('ITEM.DRAW_SHAPE_REQUIRED') || 'Please draw a shape first')
      return false
    }

    const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)

    // Store the polygon data in the POI draft state (this will trigger POI search)
    await poi.setDraftPolygon({
      name: this.name,
      geometry: this.region,
      color_id: effectiveColorId,
      transparency: this.transparency
    })

    return true
  }
}

export default Shape
